import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  AuthService,
  LicenseChildPortfolioFeatureEnum,
  LicenseGeneralFeature,
  LicenseManagementFeature,
  LicensePapFeature,
  LicenseProductType,
  LicenseService,
  LinksService,
} from '@isophi/core-legacy';
import { BaseComponent, MenuSection, MenuSectionKey, SidebarItem, SourceApp } from '@isophi/core-ui';
import { TranslocoService } from '@jsverse/transloco';
import { NgxSpinnerService } from 'ngx-spinner';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  ReplaySubject,
  shareReplay,
  startWith,
  switchMap,
  takeUntil,
} from 'rxjs';

import { RouterLinkFactory } from '../../core/router/router-link.factory';
import { HelperService } from '../../core/services/helper.service';
import { UserService } from '../../core/services/user.service';
import { SwitchToDemoKindergartenService } from '../../shared/services/switch-to-demo-kindergarten.service';

const SECTION_TITLES = {
  [MenuSectionKey.MANAGEMENT]: 'Řízení školky',
  [MenuSectionKey.CHILD_PORTFOLIO]: 'Portfolio dítěte',
  [MenuSectionKey.PAP]: 'Podpora pedagoga',
  [MenuSectionKey.KINDERGARTEN_ADMIN]: 'Ředitelna',
} as const;

const ONE_DAY_MS = 1000 * 60 * 60 * 24;
const EXPIRING_WARNING_DAYS = 30;

type LicenseBannerType = 'trial' | 'trial-expired' | 'license-expiring' | 'license-expired';
type SectionLicenseConfig = { productType: LicenseProductType; featureCodename: string };

interface LicenseBannerState {
  type: LicenseBannerType;
  daysLeft?: number;
}

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LayoutComponent extends BaseComponent implements OnInit {
  protected readonly menuSectionKey = MenuSectionKey;

  showHeader$: BehaviorSubject<boolean>;

  sourceApp: SourceApp = SourceApp.MANAGEMENT;

  menuSections: MenuSection[] = [];
  firstLevelMenuItems: SidebarItem[] = [];

  private readonly helperService = inject(HelperService);
  readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly translocoService = inject(TranslocoService);
  private readonly userService = inject(UserService);
  private readonly licenseService = inject(LicenseService);
  protected readonly routerLinkFactory = inject(RouterLinkFactory);
  private readonly switchDemoKindergartenService = inject(SwitchToDemoKindergartenService);
  private readonly linksService = inject(LinksService);
  private readonly spinner = inject(NgxSpinnerService);

  private get isManager(): boolean {
    return this.authService.loggedUser?.teacher?.isManager() ?? false;
  }

  private get isDirector(): boolean {
    return this.authService.loggedUser?.teacher?.isDirector() ?? false;
  }

  private readonly routeUrl$ = this.router.events.pipe(
    filter((e): e is NavigationEnd => e instanceof NavigationEnd),
    map((e) => e.urlAfterRedirects || e.url),
    startWith(this.router.url),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private hasTestingProduct = false;
  private showDemoKindergarten = false;
  private showPapApp = false;
  private showChildPortfolio = false;
  private showTeacherApp = false;
  private showManagement = false;
  private licensesLoaded = false;
  private isManagementExpired = false;
  private isChildPortfolioExpired = false;
  private isPapExpired = false;
  private currentUrl = '';

  private readonly lockedManagementItemUrls = new Set<string>();
  private lockedManagementRoutePrefixes: string[] = [];
  private readonly lockedChildPortfolioItemUrls = new Set<string>();
  private lockedChildPortfolioRoutePrefixes: string[] = [];
  private readonly lockedPapItemUrls = new Set<string>();
  private lockedPapRoutePrefixes: string[] = [];

  showManagementLockOverlay = false;
  showChildPortfolioLockOverlay = false;
  showPapLockOverlay = false;
  lockModuleNameKey = 'shared/managementApp';

  licenseBanner: LicenseBannerState | null = null;

  protected readonly sectionKey$ = new BehaviorSubject<MenuSectionKey | null>(null);

  private readonly trialLicenseFeatureBySection: Partial<Record<MenuSectionKey, SectionLicenseConfig>> = {
    [MenuSectionKey.MANAGEMENT]: {
      productType: LicenseProductType.MANAGEMENT,
      featureCodename: LicenseManagementFeature.TRIAL,
    },
    [MenuSectionKey.CHILD_PORTFOLIO]: {
      productType: LicenseProductType.CHILD_PORTFOLIO,
      featureCodename: LicenseGeneralFeature.TRIAL,
    },
    [MenuSectionKey.PAP]: {
      productType: LicenseProductType.PAP,
      featureCodename: LicensePapFeature.TRIAL,
    },
  };

  private readonly paidLicenseFeatureBySection: Partial<Record<MenuSectionKey, SectionLicenseConfig>> = {
    [MenuSectionKey.MANAGEMENT]: {
      productType: LicenseProductType.MANAGEMENT,
      featureCodename: LicenseManagementFeature.ACCESS_MANAGEMENT,
    },
    [MenuSectionKey.CHILD_PORTFOLIO]: {
      productType: LicenseProductType.CHILD_PORTFOLIO,
      featureCodename: LicenseChildPortfolioFeatureEnum.ACCESS_PORTFOLIO,
    },
    [MenuSectionKey.PAP]: {
      productType: LicenseProductType.PAP,
      featureCodename: LicensePapFeature.ACCESS_PAP,
    },
  };

  protected readonly tAppUrl = this.linksService.getTeacherAppUrl();

  protected readonly sidebarChangesTrigger$ = new ReplaySubject<void>();

  private get isAnyRawLockOverlay(): boolean {
    return this.showManagementLockOverlay || this.showChildPortfolioLockOverlay || this.showPapLockOverlay;
  }

  get showAnyLockOverlay(): boolean {
    return this.licensesLoaded && this.isAnyRawLockOverlay;
  }

  get showLockCheckLoadingOverlay(): boolean {
    return !this.licensesLoaded && this.isAnyRawLockOverlay;
  }

  ngOnInit(): void {
    this.showHeader$ = this.helperService.showHeader$;
    this.initLockConfigs();
    this.observeRouteState();
    this.observeLicenseVisibilityAndLocks();
    this.observeLicenseBannerState();
  }

  private setupMenuSections(): void {
    // Don't setup menu sections until licenses are loaded
    if (!this.licensesLoaded) {
      this.menuSections = [];
      return;
    }

    const isManagementLocked = this.isManagementExpired || !this.showManagement;
    const isChildPortfolioLocked = this.isChildPortfolioExpired || !this.showChildPortfolio;
    const isPapLocked = this.isPapExpired || !this.showPapApp;

    const managementItems = this.helperService.managementMenuItems.map((item) => ({
      ...item,
      locked: isManagementLocked && this.lockedManagementItemUrls.has(item.url),
    }));
    const childPortfolioItems = this.helperService.childrenPortfolioMenuItems.map((item) => ({
      ...item,
      locked: isChildPortfolioLocked && this.lockedChildPortfolioItemUrls.has(item.url),
    }));
    const papItems = this.helperService.papMenuItems.map((item) => ({
      ...item,
      locked: isPapLocked && this.isLockedPapItemUrl(item.url),
    }));

    this.menuSections = [
      {
        sectionKey: MenuSectionKey.MANAGEMENT,
        title: SECTION_TITLES[MenuSectionKey.MANAGEMENT],
        items: managementItems,
      },
      {
        sectionKey: MenuSectionKey.CHILD_PORTFOLIO,
        title: SECTION_TITLES[MenuSectionKey.CHILD_PORTFOLIO],
        items: childPortfolioItems,
      },
      {
        sectionKey: MenuSectionKey.PAP,
        title: SECTION_TITLES[MenuSectionKey.PAP],
        items: papItems,
      },
    ];

    // Only add kindergarten admin section if user is a manager
    if (this.isManager) {
      this.menuSections.push({
        sectionKey: MenuSectionKey.KINDERGARTEN_ADMIN,
        title: SECTION_TITLES[MenuSectionKey.KINDERGARTEN_ADMIN],
        items: this.helperService.kindergartenAdminMenuItems(this.hasTestingProduct, this.isDirector, this.isManager),
      });
    }

    this.menuSections.push({
      sectionKey: MenuSectionKey.HELP,
      title: SECTION_TITLES[MenuSectionKey.HELP],
      items: [],
    });
  }

  // Method to be passed as callback to sidebar component
  onNavigationChange = (currentUrl: string): SidebarItem[] => {
    return this.generateFirstLevelMenuItems(currentUrl);
  };

  private setupFirstLevelMenuItems(): void {
    const currentUrl = this.router ? this.router.url.split('?')[0] : window.location.pathname;
    this.firstLevelMenuItems = this.generateFirstLevelMenuItems(currentUrl);
  }

  private getActiveFirstLevelSectionFromUrl(url: string): MenuSectionKey {
    // Normalize empty/root to management default
    if (url === '/' || url === '') {
      return MenuSectionKey.MANAGEMENT;
    }

    const managementPrefix = this.routerLinkFactory.dashboard()[0]; // '/management'
    const homeFullPath = this.routerLinkFactory.homepage().join('/'); // '/home'
    const childPortfolioPrefix = this.routerLinkFactory.childrenPortfolio()[0]; // '/children-portfolio'
    const papPrefix = this.routerLinkFactory.papPortfolio()[0]; // '/pap'
    const settingsPrefix = this.routerLinkFactory.settings()[0]; // '/settings'
    const helpFullPath = this.routerLinkFactory.help().join('/'); // '/management/help'

    // Exact homepage path first
    if (url === homeFullPath) {
      return MenuSectionKey.HOME;
    }

    if (url.startsWith(childPortfolioPrefix)) {
      return MenuSectionKey.CHILD_PORTFOLIO;
    }
    if (url.startsWith(papPrefix)) {
      return MenuSectionKey.PAP;
    }
    if (url.startsWith(settingsPrefix)) {
      return MenuSectionKey.KINDERGARTEN_ADMIN;
    }
    if (url.startsWith(helpFullPath)) {
      return MenuSectionKey.HELP;
    }
    if (url.startsWith(managementPrefix)) {
      return MenuSectionKey.MANAGEMENT;
    }

    // Fallback
    return MenuSectionKey.MANAGEMENT;
  }

  private generateFirstLevelMenuItems(currentUrl: string): SidebarItem[] {
    // Don't render menu items until licenses are loaded to prevent flickering
    if (!this.licensesLoaded) {
      return [];
    }

    const activeSection = this.getActiveFirstLevelSectionFromUrl(currentUrl);

    const items: SidebarItem[] = [];

    items.push({
      icon: 'home.svg',
      label: this.translocoService.translate('shared/dashboard'),
      url: this.routerLinkFactory.homepage().join('/'),
      selected: activeSection === MenuSectionKey.HOME,
    });

    // Add management item only if user has management product
    if (this.showManagement) {
      items.push({
        icon: 'management.svg',
        label: this.translocoService.translate('shared/managementApp'),
        url: this.routerLinkFactory.dashboard().join('/'),
        selected: activeSection === MenuSectionKey.MANAGEMENT,
      });
    }

    // Add child portfolio item only if user has child portfolio product
    if (this.showChildPortfolio) {
      items.push({
        icon: 'child-portfolio.svg',
        selectedIcon: 'child-portfolio-selected.svg',
        label: this.translocoService.translate('childrenPortfolio/title'),
        url: this.routerLinkFactory.childrenPortfolioDashboard().join('/'),
        selected: activeSection === MenuSectionKey.CHILD_PORTFOLIO,
        divider: true,
      });
    }

    // Teacher app, only with menu_diag license (external app)
    if (this.showTeacherApp) {
      items.push({
        icon: 't-app.svg',
        label: this.translocoService.translate('teacherApp/title'),
        url: this.tAppUrl.url,
        targetId: this.tAppUrl.target,
        isHref: true,
        selected: false, // External app, never selected
      });
    }

    // Add PAP item only if user has PAP product
    if (this.showPapApp) {
      const papAppUrl = this.linksService.getPapAppUrl();
      items.push({
        icon: 'podpora-pedagoga.svg',
        label: this.translocoService.translate('shared/papApp'),
        url: this.isManager ? this.routerLinkFactory.papPortfolio().join('/') : papAppUrl.url,
        isHref: !this.isManager,
        targetId: papAppUrl.target,
        selected: this.isManager ? activeSection === MenuSectionKey.PAP : false,
        divider: true,
      });
    }

    // Only add kindergarten admin item if user is a manager
    if (this.isManager) {
      items.push({
        icon: 'school-administration.svg',
        label: this.translocoService.translate('administration/title'),
        url: this.routerLinkFactory.kindergartenData().join('/'),
        selected: activeSection === MenuSectionKey.KINDERGARTEN_ADMIN,
      });
    }

    if (!this.isDemoKindergarten) {
      items.push({
        icon: 'help.svg',
        label: this.translocoService.translate('help/title'),
        url: this.routerLinkFactory.help().join('/'),
        selected: activeSection === MenuSectionKey.HELP,
      });
    }

    return items;
  }

  get isDemoKindergarten(): boolean {
    return this.userService.loggedKindergarten?.isDemo;
  }

  get showDemoSwitch(): boolean {
    return !this.userService.loggedKindergarten?.isDemo && this.showDemoKindergarten;
  }

  switchDemoToDemoKindergarten(): void {
    this.switchDemoKindergartenService.switch();
  }

  private getNearestTrialLicenseStatus() {
    return combineLatest([
      this.licenseService.getLicenseStatus(LicenseProductType.MANAGEMENT, LicenseManagementFeature.TRIAL),
      this.licenseService.getLicenseStatus(LicenseProductType.CHILD_PORTFOLIO, LicenseGeneralFeature.TRIAL),
      this.licenseService.getLicenseStatus(LicenseProductType.PAP, LicensePapFeature.TRIAL),
    ]).pipe(
      map((licences) => {
        return licences
          .filter((licence) => !!licence.daysUntilEnd)
          .sort((a, b) => new Date(a.validTo).getTime() - new Date(b.validTo).getTime())
          ?.at(0);
      })
    );
  }

  private initLockConfigs(): void {
    this.initManagementLockConfig();
    this.initChildPortfolioLockConfig();
    this.initPapLockConfig();
  }

  private observeRouteState(): void {
    this.routeUrl$.pipe(takeUntil(this.ngUnsubscribe$)).subscribe((url) => {
      this.currentUrl = url;
      this.updateManagementLockState(url);
      this.syncLockCheckSpinner();
    });
  }

  private observeLicenseVisibilityAndLocks(): void {
    combineLatest([
      this.licenseService.canSeeTestingProduct,
      this.licenseService.canSeeDemoKindergarten,
      this.licenseService.canSeeTeacherApp,
      this.licenseService.canSeePap,
      this.licenseService.canSeeChildPortfolio,
      this.licenseService.canSeeManagement,
      this.licenseService.isModuleLocked(
        LicenseProductType.MANAGEMENT,
        LicenseManagementFeature.ACCESS_MANAGEMENT,
        LicenseManagementFeature.TRIAL
      ),
      this.licenseService.isModuleLocked(
        LicenseProductType.CHILD_PORTFOLIO,
        LicenseChildPortfolioFeatureEnum.ACCESS_PORTFOLIO,
        LicenseGeneralFeature.TRIAL
      ),
      this.licenseService.isModuleLocked(LicenseProductType.PAP, LicensePapFeature.ACCESS_PAP, LicensePapFeature.TRIAL),
    ])
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(
        ([
          hasTestingProduct,
          showDemo,
          showTeacherApp,
          showPap,
          showChildPortfolio,
          showManagement,
          isManagementLocked,
          isChildPortfolioLocked,
          isPapLocked,
        ]) => {
          this.hasTestingProduct = hasTestingProduct;
          this.showDemoKindergarten = showDemo;
          this.showTeacherApp = showTeacherApp;
          this.showPapApp = showPap;
          this.showChildPortfolio = showChildPortfolio;
          this.showManagement = showManagement;
          this.isManagementExpired = isManagementLocked;
          this.isChildPortfolioExpired = isChildPortfolioLocked;
          this.isPapExpired = isPapLocked;
          this.licensesLoaded = true;

          this.setupFirstLevelMenuItems();
          this.setupMenuSections();
          this.updateManagementLockState(this.currentUrl || this.router.url);
          this.syncLockCheckSpinner();
          this.sidebarChangesTrigger$.next();
        }
      );
  }

  private observeLicenseBannerState(): void {
    this.routeUrl$
      .pipe(
        map((url) => this.getActiveFirstLevelSectionFromUrl((url || '/').split('?')[0])),
        distinctUntilChanged(),
        switchMap((sectionKey) => {
          this.sectionKey$.next(sectionKey);

          const trialConfig = this.trialLicenseFeatureBySection[sectionKey];
          const paidConfig = this.paidLicenseFeatureBySection[sectionKey];

          if (!trialConfig || !paidConfig) {
            return this.getNearestTrialLicenseStatus().pipe(
              map((status) => {
                if (status?.active && status?.daysUntilEnd) {
                  return { type: 'trial' as LicenseBannerType, daysLeft: status.daysUntilEnd };
                }
                return null;
              })
            );
          }

          return combineLatest([
            this.licenseService.getLicenseStatus(trialConfig.productType, trialConfig.featureCodename),
            this.licenseService.getLicenseStatus(paidConfig.productType, paidConfig.featureCodename),
          ]).pipe(map(([trialStatus, paidStatus]) => this.computeBannerState(trialStatus, paidStatus)));
        }),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe((bannerState) => {
        this.licenseBanner = bannerState;
      });
  }

  private initManagementLockConfig(): void {
    const lockedMenuUrls = [
      this.routerLinkFactory.dashboard().join('/'),
      this.routerLinkFactory.attendanceToday().join('/'),
      this.routerLinkFactory.attendanceOverview().join('/'),
      this.routerLinkFactory.gradebookList().join('/'),
      this.routerLinkFactory.announcementList().join('/'),
      this.routerLinkFactory.exportMsmt().join('/'),
    ];

    this.lockedManagementItemUrls.clear();
    lockedMenuUrls.forEach((url) => this.lockedManagementItemUrls.add(url));
    this.lockedManagementRoutePrefixes = lockedMenuUrls;
  }

  private initChildPortfolioLockConfig(): void {
    const lockedMenuUrls = [
      this.routerLinkFactory.childrenPortfolio().join('/'),
      this.routerLinkFactory.childrenPortfolioShipOfKnowledge().join('/'),
      this.routerLinkFactory.childrenPortfolioClassDiary().join('/'),
      this.routerLinkFactory.childrenPortfolioKorabClass().join('/'),
      this.routerLinkFactory.childrenPortfolioSkill().join('/'),
      this.routerLinkFactory.childrenPortfolioCompetence().join('/'),
      this.routerLinkFactory.childrenPortfolioLiteracy().join('/'),
      this.routerLinkFactory.childrenPortfolioActivity().join('/'),
    ];

    this.lockedChildPortfolioItemUrls.clear();
    lockedMenuUrls.forEach((url) => this.lockedChildPortfolioItemUrls.add(url));
    this.lockedChildPortfolioRoutePrefixes = lockedMenuUrls;
  }

  private initPapLockConfig(): void {
    const papAppBaseUrl = this.linksService.getPapAppUrl({ withLogin: false }).url;
    const lockedMenuUrls = [
      this.routerLinkFactory.papPortfolio().join('/'),
      this.routerLinkFactory.papPlan().join('/'),
      this.routerLinkFactory.papAutoEvaluation().join('/'),
      papAppBaseUrl,
    ];

    this.lockedPapItemUrls.clear();
    lockedMenuUrls.forEach((url) => this.lockedPapItemUrls.add(url));
    this.lockedPapRoutePrefixes = lockedMenuUrls.filter((url) => url.startsWith('/'));
  }

  private isLockedPapItemUrl(url: string): boolean {
    if (this.lockedPapItemUrls.has(url)) {
      return true;
    }

    const papAppBaseUrl = this.linksService.getPapAppUrl({ withLogin: false }).url;
    return !!papAppBaseUrl && url.startsWith(papAppBaseUrl);
  }

  private updateManagementLockState(url: string): void {
    const cleanUrl = (url || '').split('?')[0];
    const activeSection = this.getActiveFirstLevelSectionFromUrl(cleanUrl);
    const isManagementSection = activeSection === MenuSectionKey.MANAGEMENT;
    const isLockedRoute = this.lockedManagementRoutePrefixes.some((prefix) => cleanUrl.startsWith(prefix));
    const isChildPortfolioSection = activeSection === MenuSectionKey.CHILD_PORTFOLIO;
    const isChildPortfolioLockedRoute = this.lockedChildPortfolioRoutePrefixes.some((prefix) => cleanUrl.startsWith(prefix));
    const isPapSection = activeSection === MenuSectionKey.PAP;
    const isPapLockedRoute = this.lockedPapRoutePrefixes.some((prefix) => cleanUrl.startsWith(prefix));
    const isManagementLocked = this.isManagementExpired || !this.showManagement;
    const isChildPortfolioLocked = this.isChildPortfolioExpired || !this.showChildPortfolio;
    const isPapLocked = this.isPapExpired || !this.showPapApp;

    this.showManagementLockOverlay = isManagementLocked && isManagementSection && isLockedRoute;
    this.showChildPortfolioLockOverlay = isChildPortfolioLocked && isChildPortfolioSection && isChildPortfolioLockedRoute;
    this.showPapLockOverlay = isPapLocked && isPapSection && isPapLockedRoute;

    if (this.showManagementLockOverlay) {
      this.lockModuleNameKey = 'shared/managementApp';
    } else if (this.showChildPortfolioLockOverlay) {
      this.lockModuleNameKey = 'childrenPortfolio/title';
    } else if (this.showPapLockOverlay) {
      this.lockModuleNameKey = 'shared/papApp';
    }
  }

  private syncLockCheckSpinner(): void {
    if (this.showLockCheckLoadingOverlay) {
      this.spinner.show();
    } else {
      this.spinner.hide();
    }
  }

  private computeBannerState(trialStatus: any, paidStatus: any): LicenseBannerState | null {
    const now = new Date();

    const trialActive = trialStatus?.active || false;
    const trialDaysLeft = trialStatus?.daysUntilEnd || 0;
    const trialValidTo = trialStatus?.validTo ? new Date(trialStatus.validTo) : null;
    const trialExpired = trialValidTo ? now.getTime() > trialValidTo.getTime() : false;

    const paidValidTo = paidStatus?.validTo ? new Date(paidStatus.validTo) : null;
    const paidActive = paidStatus?.active || false;
    const daysToExpiration = paidValidTo ? Math.ceil((paidValidTo.getTime() - now.getTime()) / ONE_DAY_MS) : null;
    const paidExpired = paidValidTo ? now.getTime() > paidValidTo.getTime() : false;
    const paidExpiring = paidActive && daysToExpiration > 0 && daysToExpiration <= EXPIRING_WARNING_DAYS;

    const hasSeparatePaidLicense =
      paidValidTo && trialValidTo ? paidValidTo.getTime() > trialValidTo.getTime() : !!(paidValidTo && !trialValidTo);

    // 1. Trial active → show trial countdown
    if (trialActive && trialDaysLeft) {
      return { type: 'trial', daysLeft: trialDaysLeft };
    }

    // 2. Paid license expiring → show license-expiring
    if (paidExpiring && hasSeparatePaidLicense) {
      return { type: 'license-expiring', daysLeft: daysToExpiration };
    }

    // 3. Paid license expired (separate from trial) → show license-expired
    if (paidExpired && hasSeparatePaidLicense) {
      return { type: 'license-expired' };
    }

    // 4. Trial expired, no separate paid license → show trial-expired
    if (trialExpired && !hasSeparatePaidLicense) {
      return { type: 'trial-expired' };
    }

    return null;
  }

  get shouldShowTopInfoBanners(): boolean {
    const cleanUrl = (this.currentUrl || this.router.url || '').split('?')[0];
    const homeUrl = this.routerLinkFactory.homepage().join('/');
    return !cleanUrl.startsWith(homeUrl);
  }
}
